import React, { useState, useEffect } from 'react';
import CategoriesService from '../services/categoriesService';
import { motion } from 'framer-motion';

const DynamicFieldsForm = ({ 
  subcategory, 
  categoryName, 
  values = {}, 
  onChange, 
  isVisible = true 
}) => {
  const [fieldConfig, setFieldConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFieldConfig = async () => {
      try {
        const businessData = await CategoriesService.getSubcategoryBusinessData();
        if (businessData[categoryName] && businessData[categoryName][subcategory]) {
          setFieldConfig(businessData[categoryName][subcategory]);
        } else {
          // Configuration par défaut si pas trouvé
          setFieldConfig({
            surface_piece: true,
            surface_elements: false,
            multi_pieces: false,
            nombre_elements: false
          });
        }
      } catch (error) {
        console.error('Erreur chargement config champs:', error);
        // Configuration par défaut en cas d'erreur
        setFieldConfig({
          surface_piece: true,
          surface_elements: false,
          multi_pieces: false,
          nombre_elements: false
        });
      } finally {
        setLoading(false);
      }
    };

    if (subcategory && categoryName) {
      loadFieldConfig();
    }
  }, [subcategory, categoryName]);

  const handleFieldChange = (fieldName, value) => {
    onChange(subcategory, fieldName, value);
  };

  if (loading || !fieldConfig || !isVisible) {
    return null;
  }

  const getFieldLabels = () => {
    const labels = {};
    
    if (fieldConfig.surface_piece) {
      labels.surface_piece = fieldConfig.multi_pieces 
        ? 'Surface moyenne par pièce (m²)' 
        : 'Surface totale (m²)';
    }
    
    if (fieldConfig.surface_elements) {
      labels.surface_elements = 'Surface des éléments (m²)';
    }
    
    if (fieldConfig.multi_pieces) {
      labels.multi_pieces = 'Nombre de pièces';
    }
    
    if (fieldConfig.nombre_elements) {
      labels.nombre_elements = getElementLabel();
    }
    
    return labels;
  };

  const getElementLabel = () => {
    // Personnaliser le label selon la sous-catégorie
    const subcategoryLower = subcategory.toLowerCase();
    
    if (subcategoryLower.includes('mur')) return 'Nombre de murs';
    if (subcategoryLower.includes('porte')) return 'Nombre de portes';
    if (subcategoryLower.includes('fenêtre')) return 'Nombre de fenêtres';
    if (subcategoryLower.includes('radiateur')) return 'Nombre de radiateurs';
    if (subcategoryLower.includes('prise')) return 'Nombre de prises';
    if (subcategoryLower.includes('luminaire')) return 'Nombre de luminaires';
    if (subcategoryLower.includes('placard')) return 'Nombre de placards';
    if (subcategoryLower.includes('évier')) return 'Nombre d\'éviers';
    if (subcategoryLower.includes('lavabo')) return 'Nombre de lavabos';
    if (subcategoryLower.includes('wc')) return 'Nombre de WC';
    if (subcategoryLower.includes('douche')) return 'Nombre de douches';
    if (subcategoryLower.includes('baignoire')) return 'Nombre de baignoires';
    
    return 'Nombre d\'éléments';
  };

  const labels = getFieldLabels();
  const hasFields = Object.keys(labels).length > 0;

  if (!hasFields) {
    return (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="mt-3 ml-7"
      >
        <div className="bg-yellow-600/20 border border-yellow-600/40 rounded-lg p-3">
          <p className="text-xs text-yellow-300">
            Aucune information supplémentaire requise pour cette sous-catégorie.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-3 ml-7"
    >
      <div className="space-y-3">
        {/* Champs dynamiques selon la configuration */}
        <div className="space-y-3">
          {fieldConfig.surface_piece && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {labels.surface_piece}
              </label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                placeholder={fieldConfig.multi_pieces ? "Surface moyenne" : "Surface totale"}
                value={values.surface_piece || ''}
                onChange={(e) => handleFieldChange('surface_piece', e.target.value)}
                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {fieldConfig.surface_elements && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {labels.surface_elements}
              </label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                placeholder="Surface des éléments"
                value={values.surface_elements || ''}
                onChange={(e) => handleFieldChange('surface_elements', e.target.value)}
                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {fieldConfig.multi_pieces && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {labels.multi_pieces}
              </label>
              <input
                type="number"
                min="1"
                placeholder="Nombre de pièces"
                value={values.multi_pieces || ''}
                onChange={(e) => handleFieldChange('multi_pieces', e.target.value)}
                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {fieldConfig.nombre_elements && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {labels.nombre_elements}
              </label>
              <input
                type="number"
                min="1"
                placeholder="Nombre"
                value={values.nombre_elements || ''}
                onChange={(e) => handleFieldChange('nombre_elements', e.target.value)}
                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary"
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default DynamicFieldsForm; 